//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { createSelector } from 'reselect';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { ICuralateStore } from '@models/AppState';
import { IRightsRequestState } from '@models/IItemMetadata';
import PartialCuid from '@models/PartialCuid';
import { UniversalItemType } from '@models/UniversalItemType';
import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

export interface ISplitRightsOwnedMedia {
  rightsOwnedOrPendingItems: IContentLibraryItem[];
  rightsNotOwnedItems: IContentLibraryItem[];
}

const RIGHTS_OWNED_OR_PENDING_STATUSES = new Set([
  RIGHTS_STATUS.APPROVED,
  RIGHTS_STATUS.BRAND_OWNED,
  RIGHTS_STATUS.PENDING,
  RIGHTS_STATUS.INITIATED,
]);

export const rightsOwnedOrPendingCombiner = (
  contentItems: IContentLibraryItem[],
  rightsRequestsById: Record<PartialCuid, IRightsRequestState>,
): ISplitRightsOwnedMedia => {
  if (
    contentItems.length === 0 ||
    Object.keys(rightsRequestsById).length === 0
  ) {
    return { rightsNotOwnedItems: [], rightsOwnedOrPendingItems: [] };
  }

  return contentItems.reduce(
    (acc, materializedItem) => {
      const rightsState = rightsRequestsById[
        PartialCuid.build(materializedItem)
      ] as IRightsRequestState;

      if (!rightsState) {
        acc.rightsNotOwnedItems.push(materializedItem);
        return acc;
      }

      const rightsStatus = rightsState.status;
      const communicationType = rightsState.communicationType;
      const mentionedUsers = materializedItem.socialDetails
        ? materializedItem.socialDetails.mentionedUsers
        : [];

      if (
        materializedItem.itemType === UniversalItemType.Pinterest &&
        rightsStatus === RIGHTS_STATUS.NOT_SUPPORTED
      ) {
        acc.rightsOwnedOrPendingItems.push(materializedItem);
        return acc;
      }

      if (RIGHTS_OWNED_OR_PENDING_STATUSES.has(rightsStatus)) {
        acc.rightsOwnedOrPendingItems.push(materializedItem);
      } else {
        acc.rightsNotOwnedItems.push(materializedItem);
      }

      return acc;
    },
    { rightsOwnedOrPendingItems: [], rightsNotOwnedItems: [] },
  );
};

export const getSplitRightsOwnedOrPendingMedia = createSelector(
  (state: ICuralateStore) =>
    state.galleries.addToGalleriesModal.materializedItems,
  (state: ICuralateStore) =>
    state.galleries.addToGalleriesModal.rightsRequestStates,
  rightsOwnedOrPendingCombiner,
);
